import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance'; 
import TrippinPostGrid from '../components/TrippinPostGrid';
import { jwtDecode } from 'jwt-decode';
import BottomNav from './BottomNav';
import { FaStar } from 'react-icons/fa';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, TextField } from '@mui/material';
import "../styles/profile.css"

function Profile() {
  const { userIdPar } = useParams(); 
  const navigate = useNavigate();
  const [userId, setUserId] = useState(userIdPar || null);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const ratingContainerRef = useRef(null);
  const [isBlocked, setIsBlocked] = useState(false);

  const fetchBlockStatus = async () => {
    try {
      const res = await axiosInstance.get(`/block/status`, {
        params: { targetUserId: userId },
      });
      setIsBlocked(res.data.isBlocked);
    } catch (error) {
      console.error('Error fetching block status:', error);
    }
  };

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [canViewLists, setCanViewLists] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [myRatingForUser, setMyRatingForUser] = useState(0);
  const [trippinPosts, setTrippinPosts] = useState([]);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryTrippins, setCountryTrippins] = useState([]);
  const [loadingCountryTrippins, setLoadingCountryTrippins] = useState(false);
  const [countryOffset, setCountryOffset] = useState(0);
  const COUNTRY_FETCH_LIMIT = 10;
  const hasMoreTrippins = useRef(true);
  const observer = useRef();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const res = await axiosInstance.get('/profile/countries/get', {
        params: { userId }
      });
      setCountries(res.data.countries);
      setLoadingCountries(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setLoadingCountries(false);
    }
  };
  
  const fetchTrippinsByCountry = async (country, reset = false) => {
    if (!country) {
      console.error("fetchTrippinsByCountry called with an empty country!");
      return;
    }
  
    if (loadingCountryTrippins || !hasMoreTrippins.current) return;
  
    try {
      setLoadingCountryTrippins(true);
      const viewerId = getLoggedInUserId();  
  
      if (!viewerId) {
        console.error("Viewer ID is missing. Cannot fetch Trippins.");
        return;
      }
  
      console.log(`Fetching Trippins for country: ${country} by user: ${userId}, viewer: ${viewerId}`);
  
      const res = await axiosInstance.get(`/profile/by-country/${userId}`, {
        params: {
          country: country.trim(),
          viewerId,
          limit: COUNTRY_FETCH_LIMIT,
          offset: reset ? 0 : countryOffset,
        },
      });
  
      if (res.data.length < COUNTRY_FETCH_LIMIT) {
        hasMoreTrippins.current = false;
      }
  
      const enrichedTrippins = res.data.map((trippin) => ({
        ...trippin,
        isLiked: !!trippin.isLiked,
        isSaved: !!trippin.isSaved,
        places: (trippin.places || []).map((place) => ({
          ...place,
          isSaved: !!place.is_saved,  // ✅ Ensure `isSaved` is boolean
          post_place_id: place.post_place_id,
        })),
      }));
      
  
      if (reset) {
        setCountryTrippins(enrichedTrippins);
        setCountryOffset(COUNTRY_FETCH_LIMIT);
      }
      else {
        setCountryTrippins((prev) => [...prev, ...enrichedTrippins]);
        setCountryOffset((prev) => prev + COUNTRY_FETCH_LIMIT);
      }
  
      setSelectedCountry(country);
  
      if (res.data.length > 0) {
        console.log("Navigating to TrippinPostView...");
        navigate(`/trippin/${userId}/country/${country}`, {
          state: {
            posts: enrichedTrippins.map(trippin => ({
              ...trippin,
              isLiked: !!trippin.isLiked,
              isSaved: !!trippin.isSaved,
              places: trippin.places.map(place => ({
                ...place,
                isSaved: !!place.isSaved,
                post_place_id: place.post_place_id,
              })),
            })),
            selectedPostId: enrichedTrippins[0]?.trippin_post_id, // Explicitly set first as selected
            isProfile: true, // explicitly set if required
          },
        });      
      } else {
        alert("No Trippins found for this country.");
      }
  
    } catch (err) {
      console.error("Error fetching Trippins by country:", err);
    } finally {
      setLoadingCountryTrippins(false);
    }
  };
  
  // Intersection Observer to detect when the last post is visible
  const lastTrippinElementRef = useRef(null);
  
  useEffect(() => {
    if (!selectedCountry || countryTrippins.length === 0) return; // ✅ Only observe if the user has clicked
  
    const observerCallback = (entries) => {
      if (entries[0].isIntersecting && hasMoreTrippins.current) {
        fetchTrippinsByCountry(selectedCountry);
      }
    };
  
    observer.current = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });
  
    if (lastTrippinElementRef.current) {
      observer.current.observe(lastTrippinElementRef.current);
    }
  
    return () => observer.current && observer.current.disconnect();
  }, [selectedCountry, countryTrippins]);
  
  const handleBlockUser = async () => {
    try {
      await axiosInstance.post('/block/block', { blockedId: userId });
      alert(`You have blocked user ${username}.`);
      setIsBlocked(true);
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user.');
    }
  };
  
  const handleUnblockUser = async () => {
    try {
      await axiosInstance.post('/block/unblock', { blockedId: userId });
      alert(`You have unblocked user ${username}.`);
      setIsBlocked(false);
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Failed to unblock user.');
    }
  };
  
  const handleShareProfile = () => {
    const shareUrl = `${window.location.origin}/profile/${userId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Profile link copied to clipboard!');
    });
  };
  
  const getLoggedInUserId = () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      console.error('Access token is missing.');
      return null;
    }
  
    try {
      const decodedToken = jwtDecode(accessToken);
      if (decodedToken?.user_id) {
        return decodedToken.user_id;
      }
      else {
        console.error('Invalid token structure.');
        return null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };
  
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/profile/${userId}`);
      setProfileData(res.data);
      console.log('Profile Data:', res.data);
      setIsOwner(res.data.isOwner);
      setCanViewLists(res.data.canViewLists);
      setProfilePicture(res.data.signedUrl);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setLoading(false);
    }
  };

  const handleRatingSubmit = async (newRating) => {
    try {
      const res = await axiosInstance.post(`/profile/${userId}/rate`, { rating: newRating });
      setMyRatingForUser(res.data.rating);
    } catch (err) {
      console.error('Error rating user:', err);
    }
  };

  const loadTrippinPosts = async () => {
    try {
      const res = await axiosInstance.get(`/trippin/user/${userId}?sort=newest`);
      setTrippinPosts(res.data);
    } catch (err) {
      console.error('Error fetching trippin posts:', err);
    }
  };

  const fetchMyRatingForUser = async () => {
    try {
      const raterId = getLoggedInUserId();
      const res = await axiosInstance.get(`/profile/${raterId}/for/${userId}`);
      if (res.data && typeof res.data.rating === 'number') {
        setMyRatingForUser(res.data.rating);
      }
      else {
        setMyRatingForUser(0);
      }
    } catch (err) {
      console.error('Error fetching rating:', err);
    }
  };

  useEffect(() => {
    const loggedInUserId = getLoggedInUserId();
  
    if (!loggedInUserId) {
      console.error('User not logged in. Redirecting to login.');
      navigate('/login', { replace: true });
      return;
    }
  
    if (!userIdPar) {
      setUserId(loggedInUserId);
    }
    else {
      setUserId(userIdPar);
    }
  }, [userIdPar, navigate]);

  useEffect(() => {
    if (userId) {
      fetchProfile();
      fetchCountries();
      if (userId !== getLoggedInUserId()) {
        fetchMyRatingForUser();
        fetchBlockStatus();
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!loading && profileData) {
      loadTrippinPosts();
    }
  }, [loading, profileData]);

  const openDialog = () => {
    setShowHamburgerMenu(false);
    setOpenDeleteDialog(true);
  };

  const handleDeleteAccount = async () => {
    const loggedInUserId = getLoggedInUserId();
    if (!loggedInUserId || loggedInUserId !== userId) {
      alert("Unauthorized action. You can only delete your own account.");
      return;
    }

    setError("");

    if (!password) {
      setError("Password is required.");
      return;
    }

    try {
      const res = await axiosInstance.delete(`/profile/delete`, { data: { userId: loggedInUserId, password } });
      
      if (res.status === 200) {
        localStorage.removeItem("accessToken");
        navigate("/login");
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.response?.data?.message || "Failed to delete account.");
    }
  };

  if (loading) {
    return  (
      <>
        <div className="loading-container">
          <CircularProgress size={40} sx={{ color: 'black' }} />
        </div>
        <BottomNav />
      </>
    );
  }

  if (!profileData) return <div>Profile not found</div>;

  const {
    username,
    stalking_count,
    stalkers_count,
    trippin_count,
    hasFlash,
  } = profileData;

  const openHamburgerMenu = () => setShowHamburgerMenu(true);
  const closeHamburgerMenu = () => setShowHamburgerMenu(false);

  if (loadingCountries) {
    return  <div className="loading-container">
              <CircularProgress size={40} sx={{ color: 'black' }} />
            </div>;
  }

  return (
    <div className="container">
      {/* PROFILE HEADER */}
      <div className="profile-header">
        <img
          src={profilePicture || '/assets/images/default_profile.png'}
          alt="Profile"
          className={`profile-picture ${hasFlash ? 'has-flash' : ''}`}
          onClick={() => {
            if (hasFlash) {
              navigate(`/user-flashes/${userId}`);
            }
          }}
        />

        <div className="profile-details">
          <h2 className="username">{profileData.username}</h2>
          <div className="stats">
            {canViewLists && (
              <>
                <p onClick={() => navigate(`/profile/${userId}/stalkers`)}>Stalkers {profileData.stalkers_count}</p>
                <p onClick={() => navigate(`/profile/${userId}/stalking`)}>Stalking {profileData.stalking_count}</p>
              </>
            )}
            <p>Trippins {trippin_count}</p>
          </div>
        </div>

        {(isOwner || (!isOwner && !showHamburgerMenu)) && (
          <button
            className="hamburger-menu"
            onClick={openHamburgerMenu}
          >
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </button>
        )}
      </div>

      {!isOwner && (
        <div style={{ margin: '1rem 0' }}>
          <p>Your rating for {username}:</p>
          <div
            style={{
              display: 'flex',
              position: 'relative',
              width: 'fit-content',
            }}
            ref={ratingContainerRef}
            onMouseLeave={() => setHoverRating(null)}
            onTouchMove={(e) => {
              const container = ratingContainerRef.current;
              if (!container) return;

              const rect = container.getBoundingClientRect();
              const touchX = e.touches[0].clientX - rect.left;

              const starWidth = rect.width / 5;
              const touchedStar = Math.ceil(touchX / starWidth);

              if (touchedStar < 1) {
                setHoverRating(0);
              }
              else if (touchedStar > 5) {
                setHoverRating(5);
              }
              else {
                setHoverRating(touchedStar);
              }
            }}
            onTouchEnd={() => {
              handleRatingSubmit(hoverRating || 0);
            }}
          >
            {[1, 2, 3, 4, 5].map((starValue) => (
              <label key={starValue} style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rating"
                  value={starValue}
                  style={{ display: 'none' }}
                  onClick={() => handleRatingSubmit(starValue)}
                />
                <FaStar
                  size={24}
                  color={
                    starValue <= (hoverRating || myRatingForUser)
                      ? '#ffc107'
                      : '#e4e5e9'
                  }
                  onMouseEnter={() => setHoverRating(starValue)}
                />
              </label>
            ))}
          </div>

        </div>
      )}
      {countries.length > 0 && (
        <div className="countries-list">
          <div className="profile-flags-container">
            {countries.map((isoCode, index) => (
             <div
             key={index}
             className="flag-bubble"
             onClick={() => {
              setSelectedCountry(isoCode);
              setCountryTrippins([]);
              setCountryOffset(0);
              hasMoreTrippins.current = true;
              
              fetchTrippinsByCountry(isoCode, true); // ✅ Fetch + navigate inside
            }}                   
             style={{ cursor: "pointer" }}
           >
             <img
               src={`/assets/flags/${isoCode.toLowerCase()}.svg`}
               alt={isoCode}
               className="flag-icon"
             />
           </div>           
            ))}
          </div>
        </div>
      )}


      {/* TRIPPIN POSTS GRID */}
      <div className="trippin-posts">
        {/* <h3>Trippins</h3> */}
        <TrippinPostGrid navigate={navigate} posts={trippinPosts} profilePicture={profileData.signedUrl} username={profileData.username} isOwner={isOwner} isProfile={true}/>
      </div>

      {/* HAMBURGER MENU */}
      {showHamburgerMenu && (
        <div className='hamburger-panel'>
          <button onClick={closeHamburgerMenu} style={{ color: "black", fontWeight: "bold" }}>X</button>
          <ul style={{ listStyleType: 'none', padding: 0, marginTop: '1rem' }}>
          {isOwner ? (
            <>
              <h2 className="section-title first">Account Settings</h2>
              <li>
                <button onClick={() => navigate('/profile-upload')}>Edit Profile Picture</button>
              </li>
              <li>
                <button onClick={() => navigate('/change-password')}>Change Password</button>
              </li>
              <li>
                <button onClick={() => navigate('/edit-close-friends')}>Edit Close Friends</button>
              </li>
              <li>
                <button onClick={() => navigate('/mytabs')}>My trippins</button>
              </li>
              <li>
                <button onClick={() => navigate('/tabs')}>Post a trippin</button>
              </li>
              <h2 className="section-title">Legal</h2>
              <li>
                <button onClick={() => navigate('/privacy-policy')}>Privacy Policy</button>
              </li>
              <li>
                <button onClick={() => navigate('/terms-conditions')}>Terms & Conditions</button>
              </li>

              <h2 className="section-title">Reset Options</h2>
              <li>
                <button
                  className='logout'
                  onClick={() => {
                    localStorage.removeItem('accessToken');
                    navigate('/login');
                  }}
                >
                  Logout
                </button>
              </li>
              <li>
                <button className='logout' onClick={openDialog}>Delete Account</button>
              </li>
            </>
          ) : (
            <>
              {/* If you are visiting someone else's profile */}
              <li>
                {isBlocked ? (
                  <button onClick={handleUnblockUser}>
                    Unblock User
                  </button>
                ) : (
                  <button onClick={handleBlockUser}>
                    Block User
                  </button>
                )}
              </li>
              <li>
                <button onClick={handleShareProfile}>
                  Share Profile
                </button>
              </li>
            </>
          )}
          </ul>
        </div>
      )}

      {/* DELETE ACCOUNT CONFIRMATION DIALOG */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action is irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="#2c3e50">
            Cancel
          </Button>
          <Button onClick={() => { setOpenDeleteDialog(false); setOpenPasswordDialog(true); }} color="error">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* PASSWORD CONFIRMATION DIALOG */}
      <Dialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
      >
        <DialogTitle>Confirm Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your password to confirm account deletion.
          </DialogContentText>
          <TextField
            fullWidth
            type="password"
            label="Password"
            variant="outlined"
            margin="dense"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{ textTransform: "none" }} onClick={() => setOpenPasswordDialog(false)} color="primary">
            Cancel
          </Button>
          <Button sx={{ textTransform: "none" }} onClick={handleDeleteAccount} color="error">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </div>
  );
}

export default Profile;
